import { useState, useEffect } from 'react';
import { Button, Col, FormGroup, Label, Input, FormFeedback, Card, CardBody, CardHeader } from 'reactstrap';
import { FaCheck, FaCalendarAlt, FaClipboardList, FaArrowLeft } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { createRealTimeCampaign } from "../../../slices/veloxity/pushNotification/createRealTimeCampaign";
import { createInAppCampaign } from "../../../slices/veloxity/pushNotification/createInAppCampaign";
import { showLoadingAlert, showSuccessAlert, showErrorAlert } from "../../../components/custom/sweetalertHelper";
import Swal from 'sweetalert2';


const campaignTypeOptions = [
    { value: 'realTime', label: 'Real Time Campaign' },
    { value: 'scheduled', label: 'Scheduled Campaign' },
];

const CampaignForm = ({
    setSelectedIcon,
    campaignData
}) => {
    const dispatch = useDispatch();
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [isValidDateRange, setIsValidDateRange] = useState(true);
    const [dateError, setDateError] = useState(false);
    const [campaignName, setCampaignName] = useState('');
    const [nameError, setNameError] = useState(false);
    const [dailyQuota, setDailyQuota] = useState('');
    const [totalQuota, setTotalQuota] = useState('');
    const [quotaError, setQuotaError] = useState(false);
    const [quotaComparisonError, setQuotaComparisonError] = useState(false);
    const [campaignType, setCampaignType] = useState(campaignTypeOptions[0]);
    const [delayMs, setDelayMs] = useState('');
    const [delayError, setDelayError] = useState(false);
    const [description, setDescription] = useState('');
    const [descriptionError, setDescriptionError] = useState(false);

    // Redux state
    const realTimeCampaignLoading = useSelector((state) => state.createRealTimeCampaign?.loading);
    const realTimeCampaignError = useSelector((state) => state.createRealTimeCampaign?.error);
    const realTimeCampaignSuccess = useSelector((state) => state.createRealTimeCampaign?.success);
    
    const inAppCampaignLoading = useSelector((state) => state.createInAppCampaign?.loading);
    const inAppCampaignError = useSelector((state) => state.createInAppCampaign?.error);
    const inAppCampaignSuccess = useSelector((state) => state.createInAppCampaign?.success);

    const today = new Date().toISOString().split('T')[0];

    useEffect(() => {
        if (campaignData) {
            setCampaignName(campaignData.name || '');
            setStartDate(campaignData.startDate || '');
            setEndDate(campaignData.endDate || '');
            setDailyQuota(campaignData.dailyQuota || '');
            setTotalQuota(campaignData.totalQuota || '');
            setDelayMs(campaignData.eventDefinition?.delayMs || campaignData.delayMs || '');
            setDescription(campaignData.description || '');
            
            if (campaignData.campaignType) {
                const selectedType = campaignTypeOptions.find(option => option.value === campaignData.campaignType);
                if (selectedType) {
                    setCampaignType(selectedType);
                }
            }
        }
    }, [campaignData]);

    useEffect(() => {
        if (startDate && endDate) {
            setIsValidDateRange(new Date(startDate) < new Date(endDate));
        }
    }, [startDate, endDate]);

    useEffect(() => {
        if (dailyQuota && totalQuota) {
            setQuotaComparisonError(parseInt(dailyQuota) > parseInt(totalQuota));
        }
    }, [dailyQuota, totalQuota]);

    useEffect(() => {
        if (realTimeCampaignSuccess || inAppCampaignSuccess) {
            showSuccessAlert("Success", "Campaign created successfully!");
        }
    }, [realTimeCampaignSuccess, inAppCampaignSuccess]);

    useEffect(() => {
        if (realTimeCampaignError || inAppCampaignError) {
            const errorMessage = realTimeCampaignError || inAppCampaignError;
            console.error("Campaign creation error:", errorMessage);
            
            showErrorAlert("Error", typeof errorMessage === 'string' ? errorMessage : "Failed to create campaign. Please check your inputs.");
        }
    }, [realTimeCampaignError, inAppCampaignError]);

    const getAudienceId = () => {
        if (campaignData?.selectedAudience?.originalData?.audienceId) {
            return campaignData.selectedAudience.originalData.audienceId;
        }
        if (campaignData?.selectedAudience?.value) {
            return campaignData.selectedAudience.value;
        }
        if (campaignData?.audienceId) {
            return campaignData.audienceId;
        }
        return null;
    };

    const getApplicationId = () => {
        if (campaignData?.selectedApp?.originalData?.id) {
            return campaignData.selectedApp.originalData.id;
        }
        if (campaignData?.selectedApp?.value) {
            return campaignData.selectedApp.value;
        }
        if (campaignData?.applicationId) {
            return campaignData.applicationId;
        }
        return null;
    };

    const getLicenseKey = () => {
        if (campaignData?.selectedApp?.originalData?.licence) {
            return campaignData.selectedApp.originalData.licence;
        }
        return null;
    };

    const getTargetingCriteria = () => {
        if (campaignData?.eventDefinition) {
            return campaignData.eventDefinition;
        }

        const targetingCriteria = {};
        
        if (campaignData?.profiles && campaignData.profiles.length > 0) {
            targetingCriteria.profiles = campaignData.profiles;
        }
        
        if (campaignData?.genderTypes && campaignData.genderTypes.length > 0) {
            targetingCriteria.genderTypes = campaignData.genderTypes;
        }
        
        if (campaignData?.generationTypes && campaignData.generationTypes.length > 0) {
            targetingCriteria.generationTypes = campaignData.generationTypes;
        }
        
        return targetingCriteria;
    };

    const validateForm = () => {
        let isValid = true;
        let errorMessages = [];

        if (!campaignName) {
            setNameError(true);
            isValid = false;
            errorMessages.push("Campaign name is required");
        }

        if (!startDate) {
            setDateError(true);
            isValid = false;
            errorMessages.push("Start date is required");
        }

        if (!endDate) {
            setDateError(true);
            isValid = false;
            errorMessages.push("End date is required");
        }

        if (startDate && endDate && !isValidDateRange) {
            isValid = false;
            errorMessages.push("End date should be after start date");
        }

        if (!dailyQuota) {
            setQuotaError(true);
            isValid = false;
            errorMessages.push("Daily quota is required");
        }

        if (!totalQuota) {
            setQuotaError(true);
            isValid = false;
            errorMessages.push("Total quota is required");
        }

        if (dailyQuota && totalQuota && parseInt(dailyQuota) > parseInt(totalQuota)) {
            setQuotaComparisonError(true);
            isValid = false;
            errorMessages.push("Daily quota cannot be greater than total quota");
        }

        if (campaignType.value === 'inApp' && !delayMs) {
            setDelayError(true);
            isValid = false;
            errorMessages.push("Delay is required for in-app campaigns");
        }

        const audienceId = getAudienceId();
        if (!audienceId) {
            isValid = false;
            errorMessages.push("Audience selection is required. Please go back to Who section and select an audience.");
        }

        const applicationId = getApplicationId();
        if (!applicationId) {
            isValid = false;
            errorMessages.push("Application selection is required. Please go back to Who section and select an application.");
        }

        const licenseKey = getLicenseKey();
        if (!licenseKey) {
            isValid = false;
            errorMessages.push("Application license key is missing. Please go back to Who section and select a valid application.");
        }

        if (!description) {
            setDescriptionError(true);
            isValid = false;
            errorMessages.push("Description is required");
        }

        let redirectUrl = "";
        if (campaignData?.offerType === 'URL') {
            redirectUrl = campaignData?.url || "";
            if (!redirectUrl) {
                isValid = false;
                errorMessages.push("URL is required for URL offer type. Please go back to What section and enter a URL.");
            }
        } else if (campaignData?.offerType === 'DEEP_LINK') {
            redirectUrl = campaignData?.deepLink || "";
            if (!redirectUrl) {
                isValid = false;
                errorMessages.push("Deep link is required for DEEP_LINK offer type. Please go back to What section and enter a deep link.");
            }
        }

        if (campaignData?.isRichNotification) {
            if (!campaignData.notificationTitle) {
                isValid = false;
                errorMessages.push("Notification title is required for rich notifications. Please go back to What section and enter a notification title.");
            }
            if (!campaignData.notificationBody) {
                isValid = false;
                errorMessages.push("Notification body is required for rich notifications. Please go back to What section and enter a notification body.");
            }
        }

        if (!campaignData?.timeRanges || campaignData.timeRanges.length === 0) {
            isValid = false;
            errorMessages.push("Time ranges are required. Please go back to When section and select at least one time range.");
        }

        if (!isValid) {
            Swal.fire({
                title: 'Validation Error',
                html: errorMessages.map(msg => `<div>${msg}</div>`).join(''),
                icon: 'error',
                confirmButtonText: 'OK'
            });
            return false;
        }

        return true;
    };

    const handleForReview = () => {
        if (!validateForm()) {
            return;
        }
        showLoadingAlert("Publishing Campaign", "Please wait while we publish your campaign...");

        const timeRangesFormatted = campaignData?.timeRanges?.map(range => ({
            day: range.day,
            startTime: range.ranges[0].startTime,
            endTime: range.ranges[0].endTime
        })) || [];

        let redirectUrl = "";
        if (campaignData?.offerType === 'URL') {
            redirectUrl = campaignData?.url || "";
        } else if (campaignData?.offerType === 'DEEP_LINK') {
            redirectUrl = campaignData?.deepLink || "";
        }

        const isCancelSelected = campaignData?.selectedButtons?.includes('cancel');
        const startDateTime = new Date(startDate + 'T00:00:00').toISOString();
        const endDateTime = new Date(endDate + 'T23:59:59').toISOString();

        const offerDefinition = {
            offerType: campaignData?.offerType || "URL",
            redirectUrl: redirectUrl,
            imageUrl: campaignData?.imageUrl || "",
            disableCancel: isCancelSelected ? "false" : "true",
            title: campaignData?.notificationTitle || "",
            body: campaignData?.notificationBody || "",
            pnType: campaignData?.isRichNotification ? "RICH_NOTIFICATION" : "SMART_PN"
        };
    
        let eventDefinition = {};
        
        if (campaignData?.campaignType === 'inApp' || campaignData?.campaignType === 'scheduled') {
            eventDefinition = {
                delayMs: parseInt(delayMs) || 0
            };
        } else {
            eventDefinition = getTargetingCriteria();
        }

        const campaignRequestData = {
            name: campaignName,
            description: description || campaignName,
            startDateTime: startDateTime,
            endDateTime: endDateTime,
            dailyQuota: parseInt(dailyQuota) || 0,
            totalQuota: parseInt(totalQuota) || 0,
            timeRanges: timeRangesFormatted,
            audienceId: getAudienceId(),
            applicationId: getApplicationId(),
            licenseKey: getLicenseKey(),
            offerDefinition: offerDefinition,
            eventDefinition: eventDefinition
        };

        if (campaignData?.campaignType === 'scheduled' || campaignData?.campaignType === 'inApp') {
            dispatch(createInAppCampaign(campaignRequestData))
                .then((response) => {
                    Swal.close(); 
                    showSuccessAlert("Success", "Campaign created successfully!");
                })
                .catch((error) => {
                    console.error("Error creating scheduled/in-app campaign:", error);
                    Swal.close(); 
                    showErrorAlert("Error", "Failed to create campaign: " + (error.message || "Unknown error"));
                });
        } else {
            dispatch(createRealTimeCampaign(campaignRequestData))
                .then((response) => {
                    Swal.close(); 
                    showSuccessAlert("Success", "Campaign created successfully!");
                })
                .catch((error) => {
                    console.error("Error creating real-time campaign:", error);
                    Swal.close(); 
                    showErrorAlert("Error", "Failed to create real-time campaign: " + (error.message || "Unknown error"));
                });
        }
    };

    return (
        <div className="publish-container">
            <Card className="mb-4">
                <CardHeader className="bg-light">
                    <div className="d-flex align-items-center">
                        <FaClipboardList className="me-2 text-primary" />
                        <h6 className="mb-0">Campaign Details</h6>
                    </div>
                </CardHeader>
                <CardBody>
                    <FormGroup row>
                        <Label for="campaignName" md={2}>Campaign Name <span className="text-danger">*</span></Label>
                        <Col md={10}>
                            <Input
                                id="campaignName"
                                name="campaignName"
                                placeholder="Enter campaign name"
                                type="text"
                                value={campaignName}
                                onChange={(e) => { setCampaignName(e.target.value); setNameError(false); }}
                                invalid={nameError}
                                className="shadow-sm"
                            />
                            <FormFeedback>Campaign name is required!</FormFeedback>
                        </Col>
                    </FormGroup>

                    <FormGroup row>
                        <Label for="description" md={2}>Description <span className="text-danger">*</span></Label>
                        <Col md={10}>
                            <Input
                                id="description"
                                name="description"
                                placeholder="Enter campaign description"
                                type="textarea"
                                value={description}
                                onChange={(e) => { setDescription(e.target.value); setDescriptionError(false); }}
                                invalid={descriptionError}
                                className="shadow-sm"
                            />
                            <FormFeedback>Description is required!</FormFeedback>
                        </Col>
                    </FormGroup>

                    {campaignData?.campaignType === 'inApp' && (
                        <FormGroup row>
                            <Label for="delayMs" md={2}>Delay (ms) <span className="text-danger">*</span></Label>
                            <Col md={10}>
                                <Input
                                    id="delayMs"
                                    name="delayMs"
                                    placeholder="Enter delay in milliseconds"
                                    type="number"
                                    min="0"
                                    value={delayMs}
                                    onChange={(e) => { setDelayMs(e.target.value); setDelayError(false); }}
                                    invalid={delayError}
                                    className="shadow-sm"
                                />
                                <FormFeedback>Delay is required for in-app campaigns!</FormFeedback>
                            </Col>
                        </FormGroup>
                    )}
                </CardBody>
            </Card>
            
            <Card className="mb-4">
                <CardHeader className="bg-light">
                    <div className="d-flex align-items-center">
                        <FaClipboardList className="me-2 text-primary" />
                        <h6 className="mb-0">Quota Settings</h6>
                    </div>
                </CardHeader>
                <CardBody>
                    <FormGroup row>
                        <Label for="dailyQuota" md={2}>Daily Quota <span className="text-danger">*</span></Label>
                        <Col md={10}>
                            <Input
                                id="dailyQuota"
                                name="dailyQuota"
                                placeholder="Enter daily quota"
                                type="number"
                                min="1"
                                value={dailyQuota}
                                onChange={(e) => { setDailyQuota(e.target.value); setQuotaComparisonError(parseInt(e.target.value) > parseInt(totalQuota)); }}
                                invalid={quotaComparisonError}
                                className="shadow-sm"
                            />
                            <FormFeedback>Daily quota cannot be greater than total quota!</FormFeedback>
                        </Col>
                    </FormGroup>

                    <FormGroup row>
                        <Label for="totalQuota" md={2}>Total Quota <span className="text-danger">*</span></Label>
                        <Col md={10}>
                            <Input
                                id="totalQuota"
                                name="totalQuota"
                                placeholder="Enter total quota"
                                type="number"
                                min="1"
                                value={totalQuota}
                                onChange={(e) => { setTotalQuota(e.target.value); setQuotaError(false); setQuotaComparisonError(parseInt(dailyQuota) > parseInt(e.target.value)); }}
                                invalid={quotaError || quotaComparisonError}
                                className="shadow-sm"
                            />
                            <FormFeedback>{quotaComparisonError ? "Total quota must be greater than or equal to daily quota!" : "Total quota is required!"}</FormFeedback>
                        </Col>
                    </FormGroup>
                </CardBody>
            </Card>
            
            <Card className="mb-4">
                <CardHeader className="bg-light">
                    <div className="d-flex align-items-center">
                        <FaCalendarAlt className="me-2 text-primary" />
                        <h6 className="mb-0">Campaign Schedule</h6>
                    </div>
                </CardHeader>
                <CardBody>
                    <FormGroup row>
                        <Label for="startDate" md={2}>Start Date <span className="text-danger">*</span></Label>
                        <Col md={10}>
                            <Input
                                id="startDate"
                                name="startDate"
                                placeholder="date placeholder"
                                type="date"
                                min={today}
                                value={startDate}
                                onChange={(e) => { setStartDate(e.target.value); setDateError(false); }}
                                invalid={dateError}
                                className="shadow-sm"
                            />
                            <FormFeedback>Start date is required!</FormFeedback>
                        </Col>
                    </FormGroup>

                    <FormGroup row>
                        <Label for="endDate" md={2}>End Date <span className="text-danger">*</span></Label>
                        <Col md={10}>
                            <Input
                                id="endDate"
                                name="endDate"
                                placeholder="date placeholder"
                                type="date"
                                min={startDate}
                                value={endDate}
                                onChange={(e) => { setEndDate(e.target.value); setDateError(false); }}
                                invalid={dateError || !isValidDateRange}
                                className="shadow-sm"
                            />
                            <FormFeedback>{!isValidDateRange ? "End date should be after start date." : "End date is required!"}</FormFeedback>
                        </Col>
                    </FormGroup>
                </CardBody>
            </Card>

            <div className="d-flex justify-content-between mt-4">
                <Button
                    color="secondary"
                    onClick={() => setSelectedIcon(2)}
                    style={{
                        backgroundColor: '#6c757d',
                        border: 'none',
                        borderRadius: '5px'
                    }}
                    className="shadow-sm"
                >
                    <FaArrowLeft className="me-2" /> Back: What
                </Button>
                <Button
                    color="primary"
                    onClick={handleForReview}
                    disabled={realTimeCampaignLoading || inAppCampaignLoading}
                    style={{
                        backgroundColor: '#CC1977',
                        border: 'none',
                        borderRadius: '5px'
                    }}
                    className="shadow-sm"
                >
                    {(realTimeCampaignLoading || inAppCampaignLoading) ? 'Publishing...' : 'Publish'} <FaCheck className="ms-2" />
                </Button>
            </div>

            {(realTimeCampaignError || inAppCampaignError) && (
                <div className="text-danger mt-3">
                    Error: {realTimeCampaignError || inAppCampaignError}
                </div>
            )}
            
            <style jsx="true">{`
                .publish-container {
                    width: 90%;
                    margin: 0 auto;
                    padding: 15px 0;
                }
                
                .form-control:focus {
                    border-color: #CC1977;
                    box-shadow: 0 0 0 0.2rem rgba(204, 25, 119, 0.25);
                }
                
                .btn-primary:hover {
                    background-color: #a01460 !important;
                }
                
                .btn-secondary:hover {
                    background-color: #5a6268 !important;
                }
                
                .card {
                    border-radius: 8px;
                    overflow: hidden;
                    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                    transition: all 0.3s ease;
                    margin-bottom: 20px;
                }
                
                .card:hover {
                    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
                }
                
                .card-header {
                    border-bottom: 1px solid rgba(0, 0, 0, 0.125);
                    padding: 0.75rem 1.25rem;
                }
                
                .shadow-sm {
                    box-shadow: 0 .125rem .25rem rgba(0, 0, 0, .075) !important;
                }
                
                .text-primary {
                    color: #CC1977 !important;
                }
                
                .text-danger {
                    color: #dc3545 !important;
                }
            `}</style>
        </div>
    );
};

export default CampaignForm;
